import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Check, X, Loader2, TrendingUp } from 'lucide-react';
import { Card } from '../components/ui/card';
import AbbrTip from '@/components/AbbrTip';
import InlinePopover from '@/components/InlinePopover';
import { Info } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TREND_TEMPLATE_RULES = [
  ['price_above_150_200', 'Price above the 150- and 200-day averages'],
  ['sma150_above_200', '150-day average above the 200-day'],
  ['sma200_rising_1m', '200-day average rising for at least a month'],
  ['sma50_above_150_200', '50-day average above the 150- and 200-day'],
  ['price_above_50', 'Price above the 50-day average'],
  ['above_52w_low_30pct', 'At least 30% above the 52-week low'],
  ['within_25pct_of_52w_high', 'Within 25% of the 52-week high'],
  ['rs_rank_70_plus', 'Relative-strength rank 70 or better'],
];

// Plain-English "why this rule" tips. A moving average (MA) is the
// average closing price over the last N trading days; it smooths the
// noise so you can see the trend.
export const TREND_TEMPLATE_TIPS = {
  price_above_150_200: 'The 150- and 200-day moving averages are the stock\'s 7- and 10-month average prices. Trading above both means buyers have paid more than the long-run average — the basic definition of an uptrend. Below them, the long-term trend is down or unproven.',
  sma150_above_200: 'When the shorter (150-day) average sits above the longer (200-day) one, the more recent 7 months have been stronger than the full 10 — the trend is improving, not fading. Stacked averages are the signature of a Stage 2 advance.',
  sma200_rising_1m: 'The 200-day average is the slowest line, so it only turns up after months of higher prices. Requiring it to have risen for at least a month filters out brief bounces inside a longer downtrend.',
  sma50_above_150_200: 'The 50-day average tracks the last ~10 weeks. Having it above the 150- and 200-day averages means the near-term trend is leading the long-term trend — momentum is accelerating rather than rolling over.',
  price_above_50: 'Price above the 10-week average means the stock is not correcting right now. Leaders usually hold above the 50-day during a Stage 2 run; a break below it is often the first crack in the trend.',
  above_52w_low_30pct: 'A stock still near its 52-week low has not proven it can recover. Being at least 30% off the low shows a real base has formed and a new leg up is under way rather than a dead-cat bounce.',
  within_25pct_of_52w_high: 'Winners are bought near highs, not deep in a hole. Within 25% of the 52-week high means the stock is close to breaking out, with little overhead supply from trapped sellers waiting to unload.',
  rs_rank_70_plus: 'Relative strength ranks the stock\'s 12-month price performance against every other stock in the scan (1–99). A rank of 70+ puts it in the top 30% — money is flowing into this name faster than most, which is what Stage 2 leaders look like.',
};

const fmtPct = (v) => (v == null ? 'n/a' : `${v > 0 ? '+' : ''}${Number(v).toFixed(1)}%`);

// Stock modal → Capital Analytics: Minervini Trend Template checklist for
// any non-dividend common stock, pass or fail.
export default function MinerviniTemplateCard({ ticker }) {
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    if (!ticker) return undefined;
    let alive = true;
    setState({ loading: true, data: null, error: null });
    axios.get(`${API}/stocks/${ticker}/minervini`)
      .then((r) => { if (alive) setState({ loading: false, data: r.data, error: null }); })
      .catch((e) => { if (alive) setState({ loading: false, data: null, error: e?.response?.data?.detail || 'Unavailable' }); });
    return () => { alive = false; };
  }, [ticker]);

  const m = state.data?.minervini;
  const rules = m?.rules || {};
  const passed = TREND_TEMPLATE_RULES.filter(([k]) => rules[k]).length;
  const verdict = !m ? null : m.sepa_pass ? 'sepa' : m.trend_template_pass ? 'template' : 'fail';
  const verdictStyle = {
    sepa: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-400/50',
    template: 'bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-400/50',
    fail: 'bg-muted text-muted-foreground border-border',
  }[verdict] || '';
  const verdictText = {
    sepa: 'Passes Minervini SEPA Lite',
    template: 'Passes the Trend Template · EPS growth below 20%',
    fail: `Fails the Trend Template · ${passed} of 8`,
  }[verdict] || '';

  return (
    <Card className="p-4 mt-4 border-sky-300/40 dark:border-sky-800/50" data-testid="minervini-template-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-sm font-bold flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-sky-500" />
            Minervini <AbbrTip term="Trend Template">Trend Template</AbbrTip>
          </h3>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            Eight rules that confirm a Stage-2 uptrend — the gatekeeper of Mark Minervini&apos;s <AbbrTip term="SEPA">SEPA</AbbrTip> method. Recalculated nightly after the close.
          </p>
        </div>
        {verdict && (
          <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[11px] font-bold ${verdictStyle}`} data-testid="minervini-template-verdict">
            {verdictText}
          </span>
        )}
      </div>

      {state.loading ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-3" data-testid="minervini-template-loading"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Checking the template…</div>
      ) : !m ? (
        <p className="text-xs text-muted-foreground mt-3" data-testid="minervini-template-empty">
          {state.error || 'Not enough price history to evaluate the template (needs about 10 months of daily bars).'}
        </p>
      ) : (
        <>
          <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
            {TREND_TEMPLATE_RULES.map(([k, label]) => {
              const ok = !!rules[k];
              const pending = k === 'rs_rank_70_plus' && m.rs_pending;
              return (
                <li key={k} className="flex items-center gap-2 text-xs" data-testid={`minervini-rule-${k}`}>
                  <span className={`inline-flex w-4 h-4 shrink-0 items-center justify-center rounded-full ${ok ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' : 'bg-rose-500/15 text-rose-600 dark:text-rose-400'}`}>
                    {ok ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                  </span>
                  <span className={ok ? '' : 'text-muted-foreground'}>{label}{pending ? ' (ranks after tonight\u2019s scan)' : ''}</span>
                  <InlinePopover
                    maxWidth={300}
                    ariaLabel={`Why this rule: ${label}`}
                    testId={`minervini-rule-tip-${k}`}
                    className="inline-flex shrink-0 text-muted-foreground/70 hover:text-foreground transition-colors"
                    render={() => (
                      <div className="text-xs leading-relaxed space-y-1.5 p-1">
                        <div className="font-bold text-foreground">{label}</div>
                        <p className="text-foreground/85">{TREND_TEMPLATE_TIPS[k]}</p>
                      </div>
                    )}
                  >
                    <Info className="w-3.5 h-3.5" />
                  </InlinePopover>
                </li>
              );
            })}
          </ul>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-2 text-center">
            {[
              ['RS rank', m.rs_rank ?? (m.rs_pending ? 'pending' : '—'), <AbbrTip term="RS RANK" className="uppercase">RS rank</AbbrTip>],
              ['Days in Stage 2', m.trend_template_pass ? (m.stage2_days ? `${m.stage2_days} scan day${m.stage2_days === 1 ? '' : 's'}` : 'new') : '—', <>Days in <AbbrTip term="STAGE 2" className="uppercase">Stage 2</AbbrTip></>],
              ['Off 52-wk high', m.off_high_pct != null ? `${m.off_high_pct}%` : 'n/a'],
              ['EPS YoY (latest qtr)', fmtPct(m.eps_yoy_pct)],
              ['Tight base', m.tight_base ? 'Yes' : 'No', <AbbrTip term="TIGHT BASE" className="uppercase">Tight base</AbbrTip>],
            ].map(([k, v, label]) => (
              <div key={k} className="rounded-md border bg-muted/30 p-2">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-bold">{label || k}</p>
                <p className="font-mono text-sm font-bold mt-0.5">{v}</p>
              </div>
            ))}
          </div>
          {Array.isArray(m.history) && m.history.length > 0 && (
            <div className="mt-3" data-testid="minervini-history-strip">
              <div className="flex items-center justify-between mb-1">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-bold">Trend Template · last {m.history.length} scan day{m.history.length === 1 ? '' : 's'}</p>
                <p className="text-[10px] text-muted-foreground font-mono">
                  {m.history.filter((h) => h.pass).length}/{m.history.length} passed
                </p>
              </div>
              <div className="flex gap-px h-4 rounded overflow-hidden bg-muted/40" role="img" aria-label={`Trend Template pass/fail history over ${m.history.length} scan days`}>
                {Array.from({ length: 90 - Math.min(90, m.history.length) }).map((_, i) => (
                  <span key={`pad-${i}`} className="flex-1 bg-transparent" />
                ))}
                {m.history.slice(-90).map((h) => (
                  <span
                    key={h.d}
                    title={`${h.d} · ${h.pass ? 'passed' : 'failed'}${h.rs != null ? ` · RS ${h.rs}` : ''}${h.sepa ? ' · SEPA' : ''}`}
                    className={`flex-1 min-w-[2px] ${h.pass ? (h.sepa ? 'bg-emerald-500' : 'bg-emerald-400/70') : 'bg-rose-400/70'}`}
                  />
                ))}
              </div>
              <div className="flex justify-between text-[9px] text-muted-foreground mt-0.5 font-mono">
                <span>{m.history.length >= 90 ? m.history[0].d : '90-day window'}</span>
                <span>{m.history[m.history.length - 1].d}</span>
              </div>
            </div>
          )}
          <p className="text-[11px] text-muted-foreground mt-2 leading-snug">
            {m.eps_accelerating != null && (m.eps_accelerating ? 'Earnings growth is accelerating versus the prior quarter. ' : 'Earnings growth is not accelerating versus the prior quarter. ')}
            {m.tight_base ? 'Tight base flagged — a VCP-style contraction candidate; confirm on the chart. ' : ''}
            {m.trend_template_pass && m.stage2_since ? `Held Stage 2 since ${m.stage2_since}. ` : ''}
            <Link to="/learn/minervini-sepa" className="font-semibold text-amber-600 dark:text-amber-400 hover:underline" data-testid="minervini-template-guide-link">Read the guide →</Link>
          </p>
        </>
      )}
    </Card>
  );
}
